<script setup>

import {computed, nextTick, reactive, ref} from "vue";
import {router, useForm} from "@inertiajs/vue3";
import BaseInputLabeled from "@/Components/UI/inputs/BaseInputLabeled.vue";
import BaseSettingsForm from "@/Components/UI/forms/DefaultSettingsForm.vue";
import FormError from "@/Components/UI/forms/FormError.vue";
import ConfirmAction from "@/Components/UI/modals/ConfirmAction.vue";
import BaseModal from "@/Components/UI/modals/BaseModal.vue";
import OrganizationAccountForm from "@/Components/organization/OrganizationAccountForm.vue";
import BaseButton from "@/Components/UI/buttons/BaseButton.vue";
import OrganizationData from "@/Components/organization/OrganizationData.vue";
import notifyUser from "@/helpers/notifyUser.js";
import parseUserCertificates from "@/helpers/sign/parseUserCertificates.js";
import createValidOpensslSignature from "@/helpers/sign/createValidOpensslSignature.js";
import CertificateList from "@/Components/documents/CertificateList.vue";
import HelpTooltip from "@/Components/UI/HelpTooltip.vue";

const props = defineProps({
    isArchive: Boolean,
});
const emit = defineEmits(['submit']);

const defaults = {
    signature: "",
    fact_address: "",
    token: "",
}

const hasCreatePermission = true;

const form = useForm(defaults);
const updatable = ref(hasCreatePermission);
let currentId = ref(null);
const certificates = reactive({
    list: [],
    chosen: null,
});
const prepareCertificates = async (retries = 0) => {
    try {
        certificates.list = (await parseUserCertificates()).filter(c => c?.subject['ИННЮЛ'] || c?.subject['ОГРНИП']);
        const validCount = certificates.list.filter(c => !c?.invalid).length;

        if (validCount === 0) {
            notifyUser('Для добавления организации или ИП необходимо наличие УКЭП');
        }
    } catch (e) {
        console.log(e);
        if (!currentId.value) {
            if (e?.message.includes('0x000004C7')) {
                notifyUser('Для создания организации необходим доступ к ЭЦП');
            } else {
                notifyUser('Необходимо настроить браузер для работы с ЭЦП, чтобы создать организацию');
            }
        }
        if (retries < 3) {
            prepareCertificates(retries + 1);
        }
    }
}

const info = reactive({
    accounts: [],
    representatives: [],
});

const fillData = (organization, force) => {
    form.processing = false;
    certificates.chosen = null;

    if (!('id' in organization)) {
        if (currentId.value === null && !force) {
            prepareCertificates();
            return
        }
        currentId.value = null
        updatable.value = hasCreatePermission;
        prepareCertificates();
    } else {
        if (currentId.value === organization.id && !force) {
            return
        }
        updatable.value = organization.permissions.update || (props.isArchive && organization.permissions.restore);
        currentId.value = organization.id;
    }

    // noinspection JSCheckFunctionSignatures
    form.defaults(Object.assign(JSON.parse(JSON.stringify(defaults)), organization))
    form.reset();
    info.accounts = JSON.parse(JSON.stringify(organization?.accounts || []));
    info.representatives = JSON.parse(JSON.stringify(organization?.representatives || []));
    form.clearErrors();
}

defineExpose({fillData});

const submit = async () => {
    form.processing = true;

    const tokenResponse = await axios.post(route('organizations.generate_token'));
    form.token = tokenResponse.data.token;

    if (certificates.chosen?.thumbprint) {
        form.signature = await createValidOpensslSignature(certificates.chosen.thumbprint, form.token);
    } else if (currentId.value === null) {
        notifyUser('Для создания организации необходимо выбрать сертификат ЭЦП');
        prepareCertificates();
        form.processing = false;

        return;
    }

    const opts = {
        preserveScroll: true,
        onSuccess: () => {
            emit('submit');
        },
        onFinish: () => {
            router.reload({
                only: ['errors', 'organizations', 'flash'],
            });
            form.processing = false;
        }
    };
    if (currentId.value === null) {
        form.post(route('organizations.store'), opts);
    } else if (!props.isArchive) {
        form.put(route('organizations.update', currentId.value), opts);
    } else {
        form.put(route('organizations.restore', currentId.value), opts)
    }
};
const readOnly = computed(() => props.isArchive || !updatable.value);

const deleteAccount = (id) => {
    form.delete(route('organizations.accounts.destroy', {
        organization: currentId.value,
        account: id,
    }), {
        only: ['errors', 'organizations', 'flash'],
        preserveScroll: true,
        onSuccess: () => {
            let idx = info.accounts.findIndex(acc => acc.id === id);
            info.accounts.splice(idx, 1);
        }
    });
}

const accountForm = ref(null);
const accountVisible = ref(false);
const chooseAccount = async (account) => {
    // PrimeVue Dialog renders the form slot only when visible, so show first, then fill.
    accountVisible.value = true;
    await nextTick();
    accountForm.value.fillData(currentId.value, account, false);
}
const handleAccountSubmit = (account) => {
    accountVisible.value = false;

    let idx = info.accounts.findIndex(acc => acc.id === account.id);
    if (!~idx) {
        info.accounts.push(account);
        return;
    }

    info.accounts[idx] = JSON.parse(JSON.stringify(account));
}
</script>

<template>
    <BaseModal :show="accountVisible"
               max-width="md"
               title="Счет организации"
               @close="accountVisible = false">
        <OrganizationAccountForm ref="accountForm"
                                 :read-only="readOnly"
                                 @submit="handleAccountSubmit"/>
    </BaseModal>

    <div class="w-full">
        <BaseSettingsForm :form="form"
                          :is-archive="isArchive"
                          :read-only="!updatable"
                          no-title
                          @submit="submit">

            <OrganizationData v-if="currentId"
                              :organization="form"/>
            <div v-else-if="certificates.list.length"
                 class="pt-5">
                <div class="flex items-center gap-1 mb-8">
                    <h4 class="font-semibold">Сертификат УКЭП:</h4>
                    <HelpTooltip text="Код сертификата для электронных подписей" />
                </div>
                <p class="text-dark-light mt-5 mb-8">
                    Требуется выбрать УКЭП руководителя организации, которую необходимо создать.
                    <br>
                    Мы подпишем уникальный код, чтобы проверить полномочия и гарантировать безопасность.
                </p>
                <CertificateList v-model:chosen="certificates.chosen"
                                 :list="certificates.list"
                                 class="mb-0"
                                 full/>
                <FormError :error="form.errors.signature"/>
                <FormError :error="form.errors.token"/>
            </div>

            <div class="pt-5">
                <div class="flex items-center gap-1 mb-8">
                    <h4 class="font-semibold">Фактический адрес:</h4>
                    <HelpTooltip text="Адрес офиса или место нахождения" />
                </div>
                <BaseInputLabeled v-model="form.fact_address"
                                  :disabled="readOnly"
                                  :error="form.errors.fact_address"
                                  label="Фактический адрес"/>
            </div>
        </BaseSettingsForm>

        <div v-if="currentId && (info.accounts.length || !readOnly)"
             class="mt-10 pt-5 w-full border-t border-dashed border-t-light-1">
            <div class="flex items-center gap-1">
                <h3 class="font-semibold">
                    Счета организации
                </h3>
                <HelpTooltip text="Банковские счета для автозаполнения в документах" />
            </div>
            <div v-for="account in info.accounts"
                 :key="account.id"
                 class="w-full flex items-center justify-between py-5">
                <div>
                    {{ account.name || account.payment_account }}
                </div>

                <div class="flex justify-end gap-10">
                    <p class="cursor-pointer text-accent-blue underline"
                       @click="chooseAccount(account)">
                        <template v-if="readOnly">Подробнее</template>
                        <template v-else>Редактировать</template>
                    </p>

                    <ConfirmAction v-if="!readOnly"
                                   btn="Удалить"
                                   desc="Это действие необратимо!"
                                   text="Вы уверены, что хотите удалить счет?"
                                   @confirmed="deleteAccount(account.id)">
                        <p class="underline text-accent-red cursor-pointer text-right">Удалить</p>
                    </ConfirmAction>
                </div>
            </div>

            <BaseButton variant="secondary" v-if="!readOnly"
                                 :disabled="form.processing"
                                 class="mt-10 w-full"
                                 @click="chooseAccount({})">
                Создать счет
            </BaseButton>
        </div>
    </div>
</template>
